import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CheckIcon, CloseIcon, CreatorCombobox, ErrorState, LoadingState, Modal, PlusIcon, SearchIcon, Switch, useConfirm, useToast, type ConfirmOptions } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe, type CreatorPhotos } from "@signet/web-kit";
import { matchAccount, parseReports, total, type Pages, type Report } from "../shared/flow.ts";
import { addAccount, BASE, dayLabel, fetchEntry, shiftDay, removeAccount, saveEntry, subsKeys, timeLabel, type Entry } from "./api.ts";
import { DayStepper, SubsTabs } from "./page.tsx";
import { SaveBar, useUnsaved } from "./save-bar.tsx";
import { BrandMark } from "../../creators/web/brand-marks.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Enter subs: every tracked creator, one row each, with a box for the free
   page, the VIP page and the third page. A blank box is a page with nothing
   to report ("--" in the message). Tab moves across a row and on to the
   next, so the list can be typed straight down. The day's message can be
   pasted instead: it opens the day it names ("OF STATS UPDATE FOR October
   11") and fills the boxes to check before saving. Several days' messages
   pasted at once (catching up on history) are saved day by day, never over
   numbers already there. Nothing typed is saved until Save on the bar that
   rises once something has changed (save-bar.tsx). For one day (2026-10-21)
   typing saved itself so Sub Flow filled in live; Jay took that back the
   next day, as the board then climbed creator by creator mid-entry.

   Who is listed is Creators' to say (Jay, 2026-10-19): every active creator,
   nobody marked as left, and no adding or taking off creators here. Only a
   creator's Fansly page is added or taken off on this page.
   ────────────────────────────────────────────────────────────────────────── */

type Draft = Record<string, { free: string; vip: string; third: string }>;
const FIELDS = ["free", "vip", "third"] as const;

const text = (n: number | null) => (n === null ? "" : String(n));
const toNumber = (s: string): number | null => {
  const t = s.trim();
  if (!t || t === "-" || t === "--") return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : null;
};
const valid = (s: string) => {
  const t = s.trim().replace(/,/g, "");
  return !t || t === "-" || t === "--" || /^-?\d{1,10}$/.test(t);
};

type Row = Entry["accounts"][number];

/**
 * What a row's boxes hold. OnlyFans: the day's new subs per page. Fansly: the
 * page's follower and subscriber counts as read off it (Jesu, 2026-10-14); the
 * server works out the day's growth from those and the day before's.
 */
const boxesOf = (a: Row): Pages => (a.platform === "fansly" ? { free: a.freeTotal, vip: a.vipTotal, third: null } : { free: a.free, vip: a.vip, third: a.third });

function draftOf(entry: Entry): Draft {
  return Object.fromEntries(entry.accounts.map((a) => { const b = boxesOf(a); return [a.id, { free: text(b.free), vip: text(b.vip), third: text(b.third) }]; }));
}

const pagesOf = (d: Draft[string] | undefined): Pages => ({ free: toNumber((d?.free ?? "").replace(/,/g, "")), vip: toNumber((d?.vip ?? "").replace(/,/g, "")), third: toNumber(d?.third ?? "") });
const same = (a: Pages, b: Pages) => a.free === b.free && a.vip === b.vip && a.third === b.third;

/** What is sent for a row: a Fansly row's boxes are its counts. */
const valueOf = (a: Row, d: Draft[string] | undefined) => {
  const p = pagesOf(d);
  return a.platform === "fansly" ? { account: a.id, free: null, vip: null, third: null, freeTotal: p.free, vipTotal: p.vip } : { account: a.id, ...p };
};

const signed = (n: number) => `${n > 0 ? "+" : ""}${n.toLocaleString()}`;

export function EnterPage() {
  const search = useSearch({ strict: false } as never) as { day?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useMe();
  const photos = useCreatorPhotos(`${BASE}/entry`);
  const entry = useQuery({ queryKey: subsKeys.entry(search.day ?? "latest"), queryFn: () => fetchEntry(search.day) });
  const [draft, setDraft] = useState<Draft>({});
  const [pasting, setPasting] = useState(false);
  const [adding, setAdding] = useState(false);
  const { confirm, element: confirmation } = useConfirm();
  // Fansly pages in the list, on unless turned off, and a search for one creator (Jay, 2026-10-14).
  // Both only narrow what is shown: numbers typed or pasted for hidden rows are still saved.
  const [withFansly, setWithFansly] = useState(true);
  const [find, setFind] = useState("");
  // A pasted message for another day, filled in once that day has loaded.
  const [pending, setPending] = useState<Report | null>(null);

  const data = entry.data;
  const latest = data?.latest ?? "";
  const day = data?.day ?? search.day ?? "";
  // Fresh boxes whenever another day (or a save) comes back from the server.
  useEffect(() => {
    if (!data) return;
    if (pending && pending.day === data.day) {
      setDraft(fill(pending, data, draftOf(data)));
      setPending(null);
    } else setDraft(draftOf(data));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const mayEdit = can("subs.entry:edit");
  const changed = useMemo(() => (data ? data.accounts.filter((a) => !same(pagesOf(draft[a.id]), boxesOf(a))) : []), [data, draft]);
  const blocked = !mayEdit && changed.some((a) => a.entered);
  const invalid = Object.values(draft).some((d) => FIELDS.some((f) => !valid(d[f])));
  const filled = data ? data.accounts.filter((a) => FIELDS.some((f) => toNumber(draft[a.id]?.[f] ?? "") !== null)).length : 0;
  const pageRef = useRef<HTMLDivElement>(null);
  const { alarm, nudge } = useUnsaved(changed.length > 0, pageRef);

  const save = useMutation({
    mutationFn: () => saveEntry(day, changed.map((a) => valueOf(a, draft[a.id]))),
    onSuccess: (result) => {
      queryClient.setQueryData(subsKeys.entry(search.day ?? "latest"), result.entry);
      void queryClient.invalidateQueries({ queryKey: ["subs", "flow"] });
      void queryClient.invalidateQueries({ queryKey: ["subs", "months"] });
      toast.success("Saved", `${result.saved} saved${result.cleared ? `, ${result.cleared} cleared` : ""}.`);
    },
    onError: (error: Error) => toast.error("Couldn't save", error.message),
  });

  const setDay = async (next: string) => {
    // Save or Cancel first, as on Discord (Jay, 2026-10-15).
    if (changed.length) {
      nudge();
      return false;
    }
    navigate({ to: ".", search: (next === latest ? {} : { day: next }) as never, replace: true });
    return true;
  };

  const set = (id: string, field: (typeof FIELDS)[number], value: string) => setDraft((d) => ({ ...d, [id]: { ...(d[id] ?? { free: "", vip: "", third: "" }), [field]: value } }));

  /** The report's numbers over a draft, telling the person what it couldn't place. */
  const fill = (report: Report, on: Entry, base: Draft): Draft => {
    const next = { ...base };
    const unknown: string[] = [];
    let used = 0;
    for (const line of report.lines) {
      const account = matchAccount(on.accounts, line);
      if (!account) {
        unknown.push(line.platform === "fansly" ? `${line.creator} FANSLY` : line.creator);
        continue;
      }
      next[account.id] = { free: text(line.free), vip: text(line.vip), third: text(line.third) };
      used++;
    }
    const notes = [
      unknown.length ? `Not on the list: ${unknown.join(", ")}. Creators are added in Creators and Fansly pages here, then paste again.` : "",
      report.unreadable.length ? `Couldn't read: ${report.unreadable.join("; ")}. Type those in.` : "",
    ].filter(Boolean);
    toast.info(`Filled ${used} ${used === 1 ? "creator" : "creators"}${report.day ? ` for ${dayLabel(report.day)}` : ""}`, notes.join(" ") || "Check the numbers, then Save.");
    return next;
  };

  /** Several days at once: each saved straight away, only where a creator has nothing for that day yet. */
  const saveDays = async (reports: Report[]) => {
    let days = 0;
    let saved = 0;
    let kept = 0;
    const unknown = new Set<string>();
    const unreadable: string[] = [];
    for (const report of reports) {
      const on = await fetchEntry(report.day!);
      const values = new Map<string, { account: string; freeTotal?: number | null; vipTotal?: number | null } & Pages>();
      for (const line of report.lines) {
        const account = matchAccount(on.accounts, line);
        if (!account) unknown.add(line.platform === "fansly" ? `${line.creator} FANSLY` : line.creator);
        else if (account.entered) kept++;
        else values.set(account.id, account.platform === "fansly" ? { account: account.id, free: null, vip: null, third: null, freeTotal: line.free, vipTotal: line.vip } : { account: account.id, free: line.free, vip: line.vip, third: line.third });
      }
      unreadable.push(...report.unreadable.map((l) => `${dayLabel(report.day!)}: ${l}`));
      if (values.size) {
        const result = await saveEntry(report.day!, [...values.values()]);
        saved += result.saved;
        days++;
      }
    }
    void queryClient.invalidateQueries({ queryKey: subsKeys.all });
    const notes = [
      kept ? `${kept} already there were left as they were.` : "",
      unknown.size ? `Not on the list: ${[...unknown].join(", ")}. Creators are added in Creators and Fansly pages here, then paste again.` : "",
      unreadable.length ? `Couldn't read: ${unreadable.join("; ")}.` : "",
    ].filter(Boolean);
    toast.success(`Saved ${saved} across ${days} ${days === 1 ? "day" : "days"}`, notes.join(" "));
  };

  const fillFrom = async (message: string) => {
    if (!data) return;
    const reports = parseReports(message);
    setPasting(false);
    if (!reports.length) {
      toast.error("Nothing to fill", "No lines like “● JACKIE (200) (143) (15)” in that text.");
      return;
    }
    const future = reports.find((r) => r.day && r.day > shiftDay(latest, 1));
    if (future) {
      toast.error("That day has no numbers yet", `The message says ${dayLabel(future.day!)}, which is after today.`);
      return;
    }
    if (reports.length > 1) {
      const dated = reports.filter((r) => r.day);
      const ok = await confirm({
        title: `Save ${dated.length} days?`,
        body: `${dayLabel(dated[0]!.day!)} to ${dayLabel(dated.at(-1)!.day!)}. Each day is saved as it is; creators who already have numbers for a day keep them.`,
        confirmLabel: "Save them",
      });
      if (ok) await saveDays(dated).catch((error: Error) => toast.error("Couldn't save", error.message));
      return;
    }
    const report = reports[0]!;
    if (report.day && report.day !== day) {
      if (await setDay(report.day)) setPending(report);
      return;
    }
    setDraft(fill(report, data, draft));
  };

  return (
    <div className="page subs-page subs-enter" ref={pageRef}>
      <header className="page-head">
        <div>
          <h1>Enter subs</h1>
          <p>Each creator's new subs for the day: free page, VIP page and third page. Leave a box blank when there's nothing for that page.</p>
        </div>
        <div className="subs-head-actions">
          <button type="button" className="secondary-button" onClick={() => setPasting(true)} disabled={!data}>
            Paste the message
          </button>
          {can("subs.account:create") ? (
            <button type="button" className="secondary-button" onClick={() => setAdding(true)} disabled={!data}>
              <PlusIcon size={14} /> Add Fansly page
            </button>
          ) : null}
        </div>
      </header>
      <SubsTabs on="enter" />

      {entry.isLoading ? (
        <LoadingState />
      ) : entry.isError || !data ? (
        <ErrorState title="Couldn't load the list" onRetry={() => entry.refetch()} />
      ) : (
        <>
          <div className="subs-bar">
            <DayStepper day={day} latest={latest} onDay={(next) => void setDay(next)} />
            <span className="subs-faint">
              {filled}/{data.accounts.length} filled
              {data.updatedAt ? ` · last saved ${timeLabel(data.updatedAt)}${data.updatedBy ? ` by ${data.updatedBy}` : ""}` : ""}
            </span>
            <div className="subs-enter-tools">
              <label className="subs-search">
                <SearchIcon size={14} />
                <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search creators" aria-label="Search creators" />
              </label>
              <span className="subs-fansly-toggle">
                <BrandMark platform="fansly" size={15} />
                Fansly
                <Switch checked={withFansly} onChange={setWithFansly} label="Show Fansly pages" />
              </span>
            </div>
          </div>

          <form
            id="subs-entry"
            className="panel subs-grid"
            onSubmit={(e) => {
              e.preventDefault();
              if (changed.length && !invalid && !blocked) save.mutate();
            }}
          >
            <div className="subs-grid-head" aria-hidden="true">
              <span>Creator</span>
              <span>Free</span>
              <span>VIP</span>
              <span>Third page</span>
              <span>Total</span>
            </div>
            {(() => {
              const shown = data.accounts.filter((a) => !find.trim() || a.creator.includes(find.trim().toUpperCase()));
              const row = (a: Row) => {
                const fansly = a.platform === "fansly";
                const d = draft[a.id] ?? { free: "", vip: "", third: "" };
                const locked = a.entered && !mayEdit;
                const dirty = !same(pagesOf(d), boxesOf(a));
                // Fansly: the growth the server worked out, once saved; blank until the day before has counts.
                const growth = !fansly ? (FIELDS.some((f) => toNumber(d[f]) !== null) ? total(pagesOf(d)).toLocaleString() : "")
                  : dirty ? "" : a.entered ? (a.free === null && a.vip === null ? <span className="subs-faint" title="Growth is worked out from the day before's counts, which aren't in yet.">needs the day before</span> : `${signed(a.free ?? 0)} · ${signed(a.vip ?? 0)}`) : "";
                return (
                  <div key={a.id} className={`subs-grid-row${dirty ? " dirty" : ""}${a.entered ? " entered" : ""}`}>
                    <span className="subs-who">
                      <CreatorAvatar name={a.creator} src={photos(a.creator)} size={24} />
                      <strong>{a.creator}</strong>
                      {fansly ? <span className="subs-platform">Fansly</span> : null}
                      {a.entered && !dirty ? <CheckIcon size={13} className="subs-saved" aria-label="Saved" /> : null}
                      {fansly && can("subs.account:delete") ? <RemoveButton id={a.id} name={a.creator} confirm={confirm} /> : null}
                    </span>
                    {(fansly ? (["free", "vip"] as const) : FIELDS).map((f) => (
                      <input
                        key={f}
                        className={`subs-box${valid(d[f]) ? "" : " bad"}`}
                        inputMode="numeric"
                        value={d[f]}
                        disabled={locked}
                        placeholder="-"
                        aria-label={`${a.creator}${fansly ? " Fansly" : ""} ${fansly ? (f === "vip" ? "subscribers" : "followers") : f === "third" ? "third page" : f === "vip" ? "VIP" : "free"}`}
                        onChange={(e) => set(a.id, f, e.target.value)}
                      />
                    ))}
                    {fansly ? <span /> : null}
                    <span className="subs-row-total tabular">{growth}</span>
                  </div>
                );
              };
              const fanslyRows = withFansly ? shown.filter((a) => a.platform === "fansly") : [];
              return (
                <>
                  {shown.filter((a) => a.platform !== "fansly").map(row)}
                  {fanslyRows.length ? (
                    <>
                      {/* Fansly is typed as counts, so it has its own heading (2026-10-14). */}
                      <div className="subs-grid-head subs-grid-head-fansly" aria-hidden="true">
                        <span>
                          <BrandMark platform="fansly" size={13} /> Fansly: today's counts
                        </span>
                        <span>Followers</span>
                        <span>Subscribers</span>
                        <span />
                        <span>Growth</span>
                      </div>
                      {fanslyRows.map(row)}
                    </>
                  ) : null}
                </>
              );
            })()}
          </form>
          <SaveBar
            form="subs-entry"
            open={changed.length > 0}
            alarm={alarm}
            message={blocked ? "Some of these were already saved. Changing them needs edit access." : invalid ? "Whole numbers only (a minus is fine)." : "Careful, you have unsaved changes!"}
            saving={save.isPending}
            canSave={!invalid && !blocked}
            onCancel={() => setDraft(draftOf(data))}
          />
        </>
      )}

      {pasting ? <PasteModal onClose={() => setPasting(false)} onFill={(message) => void fillFrom(message)} /> : null}
      {adding && data ? <AddModal entry={data} photos={photos} onClose={() => setAdding(false)} /> : null}
      {confirmation}
    </div>
  );
}

function PasteModal({ onClose, onFill }: { onClose: () => void; onFill: (text: string) => void }) {
  const [value, setValue] = useState("");
  return (
    <Modal
      open
      size="wide"
      title="Paste the sub flow message"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button brass" disabled={!value.trim()} onClick={() => onFill(value)}>
            Fill the boxes
          </button>
        </>
      }
    >
      <p className="subs-faint">The message as it is sent, “OF STATS UPDATE FOR …” line and all: it opens that day and fills the boxes, and nothing is saved until you press Save. Several days pasted together are saved day by day. Fansly lines are read as the page's follower and subscriber counts.</p>
      <textarea className="subs-paste" value={value} onChange={(e) => setValue(e.target.value)} rows={14} autoFocus placeholder={"HIGH SUB FLOW🔝: (81-500 FREE AND VIP SUBS)\n● ANNA (6) (95)\n● JACKIE (200) (143) (15)"} />
    </Modal>
  );
}

/** A Fansly page for a creator already on the list: Creators doesn't know who has one, so it is added here. */
function AddModal({ entry, photos, onClose }: { entry: Entry; photos: CreatorPhotos; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creator, setCreator] = useState<string | null>(null);
  const creators = useMemo(() => {
    const withFansly = new Set(entry.accounts.filter((a) => a.platform === "fansly").map((a) => a.creator));
    return entry.accounts.filter((a) => a.platform === "onlyfans" && !withFansly.has(a.creator)).map((a) => ({ id: a.creator, name: a.creator, avatar: photos(a.creator) }));
  }, [entry, photos]);
  const add = useMutation({
    mutationFn: () => addAccount({ creator: creator!, platform: "fansly" }),
    onSuccess: ({ account }) => {
      void queryClient.invalidateQueries({ queryKey: subsKeys.all });
      toast.success("Added", `${account.creator}'s Fansly page is on the list.`);
      onClose();
    },
    onError: (error: Error) => toast.error("Couldn't add", error.message),
  });
  return (
    <Modal
      open
      title="Add a Fansly page"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button brass" disabled={!creator || add.isPending} onClick={() => add.mutate()}>
            Add
          </button>
        </>
      }
    >
      <div className="subs-add">
        <CreatorCombobox creators={creators} value={creator} onChange={setCreator} allowNone={false} />
        <p className="subs-faint">Creators come from Creators. Add or bring someone back there and they appear here.</p>
      </div>
    </Modal>
  );
}

function RemoveButton({ id, name, confirm }: { id: string; name: string; confirm: (options: ConfirmOptions) => Promise<boolean> }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const remove = useMutation({
    mutationFn: () => removeAccount(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: subsKeys.all });
      toast.success("Taken off the list", `${name}'s Fansly days still count in the months.`);
    },
    onError: (error: Error) => toast.error("Couldn't remove", error.message),
  });
  return (
    <button
      type="button"
      className="icon-button ghost small subs-remove"
      // Out of the tab order: Tab goes box to box down the list and must never stop here (Jay, 2026-10-13).
      tabIndex={-1}
      aria-label={`Take ${name}'s Fansly page off the list`}
      onClick={async () => {
        if (await confirm({ title: `Take ${name}'s Fansly page off the list?`, body: "Its past days stay in the sub flow and the months.", confirmLabel: "Take off", danger: true })) remove.mutate();
      }}
    >
      <CloseIcon size={12} />
    </button>
  );
}
